import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Users, 
  Coffee, 
  Dumbbell, 
  UtensilsCrossed, 
  Palette, 
  FileText, 
  Heart 
} from 'lucide-react';

export interface TimeBlock {
  id: string;
  type: string;
  title: string;
  color: string;
  icon: React.ReactNode;
  defaultDuration: number; // in minutes
  category: string;
}

export const timeBlocks: TimeBlock[] = [
  {
    id: 'deep-work',
    type: 'deep-work',
    title: 'Deep Work',
    color: 'from-purple-500 to-indigo-600',
    icon: <Brain className="w-5 h-5" />,
    defaultDuration: 120,
    category: 'Work'
  },
  {
    id: 'meeting',
    type: 'meeting',
    title: 'Meeting',
    color: 'from-blue-500 to-cyan-600',
    icon: <Users className="w-5 h-5" />,
    defaultDuration: 60,
    category: 'Work'
  },
  {
    id: 'break',
    type: 'break',
    title: 'Break',
    color: 'from-green-500 to-emerald-600',
    icon: <Coffee className="w-5 h-5" />,
    defaultDuration: 15,
    category: 'Personal'
  },
  {
    id: 'exercise',
    type: 'exercise',
    title: 'Exercise',
    color: 'from-orange-500 to-red-600',
    icon: <Dumbbell className="w-5 h-5" />,
    defaultDuration: 45,
    category: 'Health'
  },
  {
    id: 'lunch',
    type: 'lunch',
    title: 'Lunch',
    color: 'from-amber-500 to-yellow-600',
    icon: <UtensilsCrossed className="w-5 h-5" />,
    defaultDuration: 60,
    category: 'Personal'
  },
  {
    id: 'creative-time',
    type: 'creative-time',
    title: 'Creative Time',
    color: 'from-pink-500 to-rose-600',
    icon: <Palette className="w-5 h-5" />,
    defaultDuration: 90,
    category: 'Work'
  },
  {
    id: 'admin-tasks',
    type: 'admin-tasks',
    title: 'Admin Tasks',
    color: 'from-gray-500 to-slate-600',
    icon: <FileText className="w-5 h-5" />,
    defaultDuration: 30,
    category: 'Work'
  },
  {
    id: 'personal-time',
    type: 'personal-time',
    title: 'Personal Time',
    color: 'from-violet-500 to-purple-600',
    icon: <Heart className="w-5 h-5" />,
    defaultDuration: 60,
    category: 'Personal'
  }
];

interface TimeBlockSidebarProps {
  onBlockDragStart: (block: TimeBlock) => void;
  onBlockDragEnd: () => void;
}

export function TimeBlockSidebar({ onBlockDragStart, onBlockDragEnd }: TimeBlockSidebarProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Time Blocks</h3>
        <Badge variant="secondary" className="text-xs">
          Drag to schedule
        </Badge>
      </div>

      <div className="space-y-2">
        {timeBlocks.map((block, index) => (
          <motion.div
            key={block.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            draggable
            onDragStart={() => onBlockDragStart(block)}
            onDragEnd={onBlockDragEnd}
            className="cursor-move"
          >
            <Card 
              className={`p-3 bg-gradient-to-r ${block.color} border-none text-white hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-xl`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  {block.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{block.title}</div>
                  <div className="text-xs text-white/80 flex items-center gap-1">
                    <span>{block.defaultDuration} min</span>
                    <span>•</span>
                    <span>{block.category}</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="glass-card p-3 mt-4 rounded-lg">
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-semibold">💡 Pro Tips:</p>
          <ul className="space-y-1 pl-3">
            <li>• Drag blocks onto calendar</li>
            <li>• Click to adjust duration</li>
            <li>• AI auto-categorizes titles</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
